import React from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, SubmitHandler, useWatch, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Box as GroupFields,
  Box as InlineFields,
} from '@node-space/storybook-components/dist/Box'
import { ButtonProps } from '@node-space/storybook-components/dist/components/Button'
import {
  DateOfBirth,
  DateErrorType,
  DateType,
} from '@node-space/storybook-components/dist/DateOfBirth'
import { Icon } from '@node-space/storybook-components/dist/Icon'
import { Input } from '@node-space/storybook-components/dist/Input'
import { InputLabel } from '@node-space/storybook-components/dist/FormElements'
import {
  Modal,
  ModalActions,
  ModalBody,
  ModalNavBar,
} from '@node-space/storybook-components/dist/Modal'
import { Radio } from '@node-space/storybook-components/dist/Radio'
import { Select } from '@node-space/storybook-components/dist/Select'
import {
  BusinessOwnersFormData,
  BusinessOwnersFormKeys,
  BusinessOwnersSchema,
} from '../schemas/businessOwnersSchema'
import { OwnerType, OwnerTypeOptions, OwnershipOptions, OwnershipType } from 'types/verification'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { useDateOfBirthInput } from 'hooks/useDateOfBirthInput'
import { Description } from 'components/Text'
import { TEMP_ID_TAG } from './BusinessOwners'

export type OwnerModalData = BusinessOwnersFormData & {
  id: number | string
}

type BusinessOwnerModalProps = {
  ownerData: OwnerModalData
  isEditingOwner: boolean
  onSaveOwner: (savedOwnerData: OwnerModalData) => void
  onDeleteOwner: (ownerId: number | string) => void
  onClose: () => void
}

export const BusinessOwnerModal = ({
  isEditingOwner,
  ownerData,
  onSaveOwner,
  onDeleteOwner,
  onClose,
}: BusinessOwnerModalProps) => {
  const { t } = useTranslation()

  const {
    dateOfBirthLabels,
    hasDateOfBirthErrors,
    getDateOfBirthFields,
    setDateOfBirthInputValue,
    setDateOfBirthErrors,
    getDateOfBirthErrors,
    getDateOfBirthErrorText,
    formatDateOfBirthData,
    formatDateOfBirthInput,
  } = useDateOfBirthInput()

  const { data: countryOptions = [], isLoading: isLoadingCountries } = useCountriesQuery(false)

  const ownerTypeRadioOptions: {
    value: OwnerType
    label: string
  }[] = [
    { value: OwnerTypeOptions.INDIVIDUAL, label: t('verification.businessOwners.individual') },
    { value: OwnerTypeOptions.CORPORATE, label: t('verification.businessOwners.corporate') },
  ]

  const ownershipTypeSelectOptions: {
    value: OwnershipType
    label: string
  }[] = [
    { value: OwnershipOptions.DIRECT, label: t('verification.businessOwners.direct') },
    { value: OwnershipOptions.INDIRECT, label: t('verification.businessOwners.indirect') },
  ]

  const defaultValues: BusinessOwnersFormData = {
    ...ownerData,
    dateOfBirth: formatDateOfBirthData(ownerData?.dateOfBirth),
  }

  const formSchema = BusinessOwnersSchema(t)
  const form = useForm<BusinessOwnersFormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })
  const {
    clearErrors,
    handleSubmit,
    control,
    formState: { errors },
  } = form

  const formValues = useWatch({ control })

  const { setInputField, setSelectField, setRadioField } = useFormFields<BusinessOwnersFormKeys>(
    formSchema,
    form,
    formValues
  )

  const formFields: { [key in BusinessOwnersFormKeys] } = {
    ownerType: setRadioField({
      key: 'ownerType',
      options: ownerTypeRadioOptions,
      onChangeAdditional: () => clearErrors(),
    }),
    // individual
    firstName: setInputField({ key: 'firstName', useLabelAsPlaceholder: true, showLabel: false }),
    lastName: setInputField({ key: 'lastName', useLabelAsPlaceholder: true, showLabel: false }),
    dateOfBirth: {}, // handled as custom control
    address1: setInputField({ key: 'address1', useLabelAsPlaceholder: true, showLabel: false }),
    address2: setInputField({ key: 'address2', useLabelAsPlaceholder: true, showLabel: false }),
    city: setInputField({ key: 'city', useLabelAsPlaceholder: true, showLabel: false }),
    postalCode: setInputField({ key: 'postalCode', useLabelAsPlaceholder: true, showLabel: false }),
    individualCountryCode: setSelectField({
      key: 'individualCountryCode',
      placeholder: t('formFields.chooseCountry'),
      options: countryOptions,
      showLabel: false,
    }),
    // corporate
    businessName: setInputField({ key: 'businessName' }),
    businessCountryCode: setSelectField({
      key: 'businessCountryCode',
      placeholder: t('formFields.chooseCountry'),
      options: countryOptions,
    }),
    registrationNumber: setInputField({ key: 'registrationNumber' }),
    ownershipPercentage: setInputField({
      key: 'ownershipPercentage',
      placeholder: '0',
    }),
    ownershipType: setSelectField({ key: 'ownershipType', options: ownershipTypeSelectOptions }),
  }

  const isIndividual = formValues.ownerType === OwnerTypeOptions.INDIVIDUAL

  const deleteOwnerButton: ButtonProps = {
    children: t('delete'),
    destructive: true,
    iconElement: <Icon name="TrashIcon" color="white" />,
    onClick: () => onDeleteOwner(ownerData.id),
  }

  const addButton: ButtonProps = {
    name: isEditingOwner ? 'save-owner' : 'add-owner',
    children: isEditingOwner
      ? `${t('save')}`
      : `${isIndividual ? t('addBeneficialOwner') : t('addCorporateShareholder')}`,
    type: 'submit',
    disabled:
      !!Object.keys(errors)?.length ||
      (formValues?.ownerType === OwnerTypeOptions.INDIVIDUAL && hasDateOfBirthErrors),
  }

  const onSubmit: SubmitHandler<typeof formValues> = (formValues: BusinessOwnersFormData) => {
    const id =
      ownerData?.id ??
      `${TEMP_ID_TAG}${
        formValues?.ownerType === OwnerTypeOptions.INDIVIDUAL
          ? `${formValues?.firstName} ${formValues?.lastName}`
          : formValues?.businessName
      }`

    const updatedOwner = {
      ...formValues,
      id,
      dateOfBirth:
        formValues.ownerType === OwnerTypeOptions.INDIVIDUAL
          ? formatDateOfBirthInput(formValues?.dateOfBirth)
          : '',
    }

    onSaveOwner(updatedOwner)
  }

  return (
    <Modal visible closeModal={onClose} allowOverFlow>
      <ModalNavBar title={t('addBusinessOwner')} onClose={onClose} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody spaceChildren={24}>
          {!isEditingOwner && <Radio {...formFields.ownerType} horizontal borderless={false} />}
          <Description>
            {isIndividual
              ? t('verification.businessOwners.individualDescription')
              : t('verification.businessOwners.corporateDescription')}
          </Description>

          {isIndividual ? (
            <>
              <Box>
                <InputLabel>{t('verification.businessOwners.legalNameOfPerson')}</InputLabel>
                <GroupFields flex direction="col" gapY={8}>
                  <Input {...formFields.firstName} />
                  <Input {...formFields.lastName} />
                </GroupFields>
              </Box>
              <Box>
                <Controller
                  control={control}
                  name="dateOfBirth"
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <DateOfBirth
                      date={getDateOfBirthFields(value)}
                      labels={{
                        ...dateOfBirthLabels,
                        errorMessage: getDateOfBirthErrorText(error),
                      }}
                      errors={getDateOfBirthErrors(error)}
                      onChange={(date: DateType) => onChange(setDateOfBirthInputValue(date))}
                      onValidate={(errors: DateErrorType) => setDateOfBirthErrors(errors)}
                    />
                  )}
                />
              </Box>
              <Box>
                <InputLabel>{t('verification.businessOwners.residentialAddress')}</InputLabel>
                <GroupFields flex direction="col" gapY={8}>
                  <Input {...formFields.address1} />
                  <Input {...formFields.address2} />
                  <InlineFields flex gap={8}>
                    <Box flexSize="fill" width="full">
                      <Input {...formFields.city} />
                    </Box>
                    <Box flexSize="fill" width="full">
                      <Input {...formFields.postalCode} />
                    </Box>
                  </InlineFields>
                  <Select
                    {...formFields.individualCountryCode}
                    loading={isLoadingCountries}
                    isSearchable
                  />
                </GroupFields>
              </Box>
            </>
          ) : (
            <>
              <Input {...formFields.businessName} />
              <Select
                {...formFields.businessCountryCode}
                loading={isLoadingCountries}
                isSearchable
              />
              <Input {...formFields.registrationNumber} />
              <InlineFields flex gap={16}>
                <Box flexSize="fill" width="full">
                  <Input {...formFields.ownershipPercentage} postLabel="%" inputMode="numeric" />
                </Box>
                <Box flexSize="fill" width="full">
                  <Select {...formFields.ownershipType} isSearchable />
                </Box>
              </InlineFields>
            </>
          )}
        </ModalBody>
        <ModalActions actions={isEditingOwner ? [deleteOwnerButton, addButton] : [addButton]} />
      </form>
    </Modal>
  )
}
